import { NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { type Observable, of } from 'rxjs';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { SkillsMatrixXlsxService } from '@core/tellent-skills-logic';
import type { SkillsIdeateResultJson, SkillsIdeateFlowData } from '@core/tellent-skills-types';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponentI,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DialogService,
  HeadingComponent,
  IconComponent,
  NotificationsController,
} from '@core/user-interface-legacy';

@Component({
  selector: 'tlc-skills-ideate-result-dialog',
  templateUrl: './skills-ideate-result-dialog.component.html',
  styleUrl: './skills-ideate-result-dialog.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    HeadingComponent,
    IconComponent,
    NgOptimizedImage,
    TranslatePipe,
  ],
})
export class SkillsIdeateResultDialogComponent implements DialogComponentI<
  Partial<SkillsIdeateFlowData>,
  { back: true }
> {
  private readonly t = injectTranslate();
  private readonly dialogRef = inject(DialogRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly xlsxService = inject(SkillsMatrixXlsxService);
  private readonly notificationsController = inject(NotificationsController);
  private readonly dialogService = inject(DialogService);

  protected readonly resultJson = signal<SkillsIdeateResultJson | null>(null);
  protected readonly isDownloading = signal(false);
  protected readonly hasDownloaded = signal(false);

  protected readonly skillsCount = computed(() => {
    const result = this.resultJson();
    return result ? this.xlsxService.countSkills(result) : 0;
  });

  private isNavigatingBack = false;

  public setDialogData(data: Partial<SkillsIdeateFlowData>, _triggerUpdate: boolean): void {
    if (data.resultJson) {
      this.resultJson.set(data.resultJson);
    }
    this.dialogRef.containerInstance.dialogConfig = {
      ...this.dialogRef.containerInstance.dialogConfig,
      closeGuard: () =>
        this.hasDownloaded() || this.isNavigatingBack ? of(true) : this.confirmLeave(),
    };
  }

  public downloadFile(): void {
    const result = this.resultJson();
    if (!result) return;

    this.isDownloading.set(true);
    this.xlsxService
      .download(result)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isDownloading.set(false);
          this.hasDownloaded.set(true);
        },
        error: () => {
          this.isDownloading.set(false);
          this.notificationsController.error({
            title: this.t('tlc.common.errors.unknown_error'),
          });
        },
      });
  }

  public backToStart(): void {
    this.isNavigatingBack = true;
    this.dialogRef.close({ back: true });
  }

  public close(): void {
    this.dialogRef.close();
  }

  private confirmLeave(): Observable<boolean> {
    return this.dialogService.error({
      title: this.t('tlc.positions.skills_ideate_flow.leave_confirmation.result.title'),
      message: this.t('tlc.positions.skills_ideate_flow.leave_confirmation.result.message'),
      agreeLabel: this.t('tlc.positions.skills_ideate_flow.leave_confirmation.confirm'),
      isCancelable: true,
    });
  }
}
